/**
 * Verifiers Directory/Marketplace Page
 * Browse and search for verifiers with filtering
 */

import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Box, Button, Avatar,
    Chip, TextField, InputAdornment, FormControl, InputLabel, Select,
    MenuItem, Paper, Stack, Rating, Divider, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    Alert, Pagination, CardActions, Badge
} from '@mui/material';
import {
    Search, FilterList, LocationOn, Star, Verified, Engineering,
    CheckCircle, Speed, TrendingUp, Phone, Email, Language,
    WorkspacePremium, Shield, Assessment, EmojiEvents, Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import verifierService from '../services/verifierService';
import VerifierSearchFilters from '../components/VerifierSearchFilters';
import Seo from '../components/Seo';
import AIVerifierHiringDialog from '../components/verifiers/AIVerifierHiringDialog';
import SimplifiedVerifierCard from '../components/verifiers/SimplifiedVerifierCard';
import DialogCloseButton from '../components/common/DialogCloseButton';

const VerifiersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // State
    const [verifiers, setVerifiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedVerifier, setSelectedVerifier] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [hiringDialog, setHiringDialog] = useState(false);
    const [selectedVerifierForHire, setSelectedVerifierForHire] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [userProject, setUserProject] = useState(null);
    
    // Advanced Filters (using VerifierSearchFilters component)
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('average_rating');
    
    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(12);
    
    // Load verifiers
    useEffect(() => {
        loadVerifiers();
    }, [page, searchTerm, filters, sortBy]);
    
    const loadVerifiers = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Convert verifier level to verification count range
            const getVerificationRange = (level) => {
                const ranges = {
                    '1': { min: 0, max: 20 },
                    '2': { min: 21, max: 50 },
                    '3': { min: 51, max: 100 },
                    '4': { min: 101, max: 200 },
                    '5': { min: 201, max: 999999 }
                };
                return ranges[level] || null;
            };

            const verificationRange = filters.verifierLevel ? getVerificationRange(filters.verifierLevel) : null;

            // Convert advanced filters to API format
            const apiFilters = {
                searchTerm: searchTerm || undefined,
                city: filters.location || undefined,
                serviceArea: filters.serviceAreas?.length > 0 ? filters.serviceAreas[0] : undefined,
                specialization: filters.skills?.length > 0 ? filters.skills[0] : undefined,
                minRating: filters.minRating > 0 ? filters.minRating : undefined,
                minVerifications: verificationRange?.min,
                maxVerifications: verificationRange?.max,
                isAvailable: true // Only show available verifiers
            };
            
            const pagination = {
                page,
                limit,
                sortBy,
                sortOrder: 'desc'
            };
            
            const response = await verifierService.getAllVerifiers(apiFilters, pagination);
            
            console.log('Verifiers loaded:', response);
            
            if (response && response.data && Array.isArray(response.data)) {
                setVerifiers(response.data);
                setTotalPages(response.pagination?.totalPages || 1);
            } else {
                console.warn('Unexpected verifiers response format:', response);
                setVerifiers([]);
                setTotalPages(1);
            }
            
        } catch (err) {
            console.error('Failed to load verifiers:', err);
            setError(err.message || 'Failed to load verifiers');
            setVerifiers([]);
        } finally {
            setLoading(false);
        }
    };
    
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
    };
    
    const handleOpenRequestQuotes = async () => {
        if (!user) {
            navigate('/login', { state: { from: '/verifiers' } });
            return;
        }

        const authProvider = user.authProvider || user.auth_provider || 'local';
        const emailVerified = user.emailVerified || user.email_verified;
        if (authProvider === 'local' && !emailVerified) {
            navigate('/verify-email', { state: { from: { pathname: '/verifiers' } } });
            return;
        }
        
        // One request pipeline for verifiers: the verification-request flow
        // (marketplace mode collects proposals). The old quote-request dialog
        // created a parallel negotiation pipeline with different semantics.
        navigate('/verification-requests/create');
    };
    
    // Verifiers have a public shareable profile at /verifiers/:id — send
    // viewers there instead of the in-page dialog so the URL can be shared.
    const handleViewProfile = (verifier) => {
        navigate(`/verifiers/${verifier.id}`);
    };

    const handleRequestQuoteFromCard = (verifier) => {
        if (!user) {
            navigate('/login', { state: { from: '/verifiers' } });
            return;
        }
        const authProvider = user.authProvider || user.auth_provider || 'local';
        const emailVerified = user.emailVerified || user.email_verified;
        if (authProvider === 'local' && !emailVerified) {
            navigate('/verify-email', { state: { from: { pathname: '/verifiers' } } });
            return;
        }
        // Direct-selection verification request with this verifier pre-picked
        navigate(`/verification-requests/create?verifierId=${verifier.user_id || verifier.id}`);
    };
    
    const handleRequestVerifier = async (verifier) => {
        if (user) {
            // Load user's project if available
            try {
                const response = await api.get('/projects');
                const data = response.data;
                const projects = data.data?.projects || data.projects || [];

                // Get the first active project or create a default one
                const activeProject = projects.find(p => p.status === 'active') || projects[0] || {
                    id: null,
                    title: 'New Project',
                    name: 'New Project',
                    type: 'Construction',
                    location: 'Zimbabwe',
                    description: '',
                    category: 'Construction'
                };

                setUserProject(activeProject);
            } catch (err) {
                console.error('Failed to load projects:', err);
                setUserProject({
                    id: null,
                    title: 'New Project',
                    name: 'New Project',
                    type: 'Construction',
                    location: 'Zimbabwe',
                    description: ''
                });
            }

            // Open AI-assisted direct hire dialog for both Professional and General verifiers
            setSelectedVerifierForHire(verifier);
            setHiringDialog(true);
        } else {
            // Prompt to login first
            navigate('/login', { state: { from: `/verifiers/${verifier.id}` } });
        }
    };
    
    const handleHiringComplete = (hireData) => {
        setHiringDialog(false);
        setSelectedVerifierForHire(null);
        // Navigate to dashboard or show success message
        alert('Verification request sent successfully!');
        navigate('/dashboard');
    };
    
    const handleContactEmail = (verifier) => {
        const subject = encodeURIComponent(`Inquiry about Verification Services - ${verifier.first_name} ${verifier.last_name}`);
        const body = encodeURIComponent(`Hello ${verifier.first_name},\n\nI found your profile on Eqaya and I'm interested in your verification services.\n\nBest regards`);
        window.open(`mailto:${verifier.email}?subject=${subject}&body=${body}`, '_blank');
    };
    
    const handleContactWhatsApp = (verifier) => {
        // Format phone number for WhatsApp (remove + and spaces)
        const phoneNumber = verifier.phone_number?.replace(/[^0-9]/g, '') || '';
        const message = encodeURIComponent(`Hello ${verifier.first_name}, I found your profile on Eqaya and I'm interested in your verification services.`);
        // WhatsApp Web URL format
        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    };
    
    const handleViewDetails = async (verifier) => {
        setSelectedVerifier(verifier);
        setDetailsDialog(true);
    };
    
    const getTierBadge = (tier) => {
        if (tier === 'professional') {
            return (
                <Chip
                    icon={<WorkspacePremium />}
                    label="Professional Verifier"
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                />
            );
        }
        return (
            <Chip
                icon={<CheckCircle />}
                label="General Verifier"
                color="success"
                size="small"
            />
        );
    };
    
    const getVerificationBadges = (verifier) => {
        const badges = [];
        
        if (verifier.identity_verified) {
            badges.push(
                <Tooltip key="identity" title="Identity Verified">
                    <Chip icon={<CheckCircle />} label="ID Verified" size="small" variant="outlined" />
                </Tooltip>
            );
        }
        
        if (verifier.qualifications_verified) {
            badges.push(
                <Tooltip key="qualifications" title="Qualifications Verified">
                    <Chip icon={<Verified />} label="Qualified" size="small" variant="outlined" />
                </Tooltip>
            );
        }
        
        if (verifier.background_check_completed) {
            badges.push(
                <Tooltip key="background" title="Background Check Completed">
                    <Chip icon={<Shield />} label="Background Checked" size="small" variant="outlined" />
                </Tooltip>
            );
        }
        
        if (verifier.average_rating >= 4.8) {
            badges.push(
                <Tooltip key="top-rated" title="Top Rated">
                    <Chip icon={<EmojiEvents />} label="Top Rated" size="small" color="warning" variant="outlined" />
                </Tooltip>
            );
        }
        
        return badges;
    };
    
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({});
        setPage(1);
    };
    
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Seo
                title="Find Independent Property & Project Verifiers in Zimbabwe"
                description="Book independent verifiers for property checks, site visits and project verification across Zimbabwe on Eqaya."
                canonicalPath="/verifiers"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Find Verifiers
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleOpenRequestQuotes}
                    sx={{
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0f8578 0%, #2dd46a 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: 4
                        },
                        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    Request Verification
                </Button>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Eqaya Accountability System:</strong> All verifiers are rated based on completed verifications, 
                    on-time delivery, and dispute resolution. Look for verified badges and check recent reviews from previous clients.
                </Typography>
            </Alert>
            
            {/* Search Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search by name or location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortBy}
                                label="Sort By"
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <MenuItem value="average_rating">Highest Rated</MenuItem>
                                <MenuItem value="total_verifications">Most Experienced</MenuItem>
                                <MenuItem value="created_at">Newest</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant={showFilters ? 'contained' : 'outlined'}
                            startIcon={<FilterList />}
                            onClick={() => setShowFilters(!showFilters)}
                            sx={{ height: '56px' }}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Advanced Filters Row */}
            {showFilters && (
                <Box sx={{ mb: 3 }}>
                    <VerifierSearchFilters
                        onFilterChange={handleFilterChange}
                        initialFilters={filters}
                        showTaskBasedFilters={false}
                    />
                </Box>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            
            {/* Results Count */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${verifiers.length} verifiers found`}
                </Typography>
            </Box>
            
            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}
            
            {/* Verifiers Grid */}
            {!loading && verifiers.length > 0 && (
                <>
                    <Grid container spacing={3}>
                        {verifiers.map((verifier) => (
                            <Grid item xs={12} md={6} key={verifier.id}>
                                <SimplifiedVerifierCard
                                    verifier={verifier}
                                    onViewProfile={() => handleViewProfile(verifier)}
                                    onRequestQuote={handleRequestQuoteFromCard}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
            
            {/* No Results */}
            {!loading && verifiers.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        No verifiers found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Try adjusting your filters or search term
                    </Typography>
                    <Button variant="outlined" onClick={clearFilters}>
                        Clear All Filters
                    </Button>
                </Paper>
            )}
            
            {/* AI-Assisted Direct Hire Dialog (for both Professional and General Verifiers) */}
            {hiringDialog && selectedVerifierForHire && userProject && (
                <AIVerifierHiringDialog
                    open={hiringDialog}
                    onClose={() => {
                        setHiringDialog(false);
                        setSelectedVerifierForHire(null);
                        setUserProject(null);
                    }}
                    project={userProject}
                    onComplete={(data) => {
                        console.log('Hire request sent successfully:', data);
                        setHiringDialog(false);
                        setSelectedVerifierForHire(null);
                        setUserProject(null);
                        alert('Hire request sent successfully! The verifier will be notified.');
                        // Optionally navigate to dashboard
                        // navigate('/dashboard');
                    }}
                />
            )}

            {/* Verifier Details Dialog */}
            {detailsDialog && selectedVerifier && (
                <Dialog
                    open={detailsDialog}
                    onClose={() => {
                        setDetailsDialog(false);
                        setSelectedVerifier(null);
                    }}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogCloseButton onClick={() => { setDetailsDialog(false); setSelectedVerifier(null); }} />
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                                    <Shield />
                                </Avatar>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6">
                                            {selectedVerifier.first_name} {selectedVerifier.last_name}
                                        </Typography>
                                        {(selectedVerifier.identity_verified || selectedVerifier.qualifications_verified) && (
                                            <Verified color="primary" />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getTierBadge(selectedVerifier.tier)}
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedVerifier.total_verifications || 0} verifications completed
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </DialogTitle>
                    
                    <DialogContent dividers>
                        {/* Rating */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Rating & Reviews
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating value={parseFloat(selectedVerifier.average_rating) || 0} precision={0.1} readOnly />
                                <Typography variant="h6">
                                    {parseFloat(selectedVerifier.average_rating || 0).toFixed(1)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({selectedVerifier.total_reviews || 0} reviews)
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Bio */}
                        {selectedVerifier.bio && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    About
                                </Typography>
                                <Typography variant="body2">
                                    {selectedVerifier.bio}
                                </Typography>
                            </Box>
                        )}
                        
                        {/* Specializations */}
                        {selectedVerifier.specializations && selectedVerifier.specializations.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Specializations
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {selectedVerifier.specializations.map((spec, index) => (
                                        <Chip key={index} label={spec} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        
                        {/* Service Areas */}
                        {selectedVerifier.service_areas && selectedVerifier.service_areas.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Service Areas
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {selectedVerifier.service_areas.map((area, index) => (
                                        <Chip key={index} icon={<LocationOn />} label={area} size="small" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        
                        {/* Experience & Stats */}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h5" color="primary">
                                        {selectedVerifier.years_experience || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Years Experience
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h5" color="primary">
                                        {parseFloat(selectedVerifier.completion_rate || 0).toFixed(0)}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Completion Rate
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        
                        {/* Verification Badges */}
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Verifications
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {getVerificationBadges(selectedVerifier)}
                            </Stack>
                        </Box>
                    </DialogContent>
                    
                    <DialogActions>
                        <Button onClick={() => setDetailsDialog(false)}>
                            Close
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => {
                                setDetailsDialog(false);
                                handleRequestVerifier(selectedVerifier);
                            }}
                        >
                            Hire Verifier
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Container>
    );
};

export default VerifiersPage;
